/**
 * Phase A R2 — DJ engine.
 *
 * Adds:
 *   - Phantom (virtual) playhead per deck for slip-aware loops / hot cues
 *   - 4 decks (A, B, C, D) with per-deck crossfader assign ('A'|'B'|'thru')
 *   - Loop rolls (`startLoop(beats, momentary)` / `releaseLoop()`)
 *   - Slip toggle (`slipActive`) — hot cue jumps + loop releases honor it
 *   - Tap-tempo (`Deck.tapTempo()`) fallback when BPM detection fails
 *   - Shared mixer FX rack: Echo, Reverb (synthesized IR), Delay, LPF, HPF
 *     with per-FX enable, dry/wet, time/size/cutoff knobs, and send-from
 *     selector (A|B|C|D|Master)
 *
 * Engine is owned by the main room tab (Room.jsx via useDjEngine). The
 * DJ board pop-out controls it across BroadcastChannel via RemoteEngine.
 */

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function crossfaderGains(x, mode = "smooth") {
  // x ∈ [-1, 1].  -1 = full A, 0 = both, +1 = full B
  if (mode === "sharp") {
    return [x <= 0 ? 1 : Math.max(0, 1 + 2 * (-x)), x >= 0 ? 1 : Math.max(0, 1 + 2 * x)];
  }
  // smooth equal-power crossfade
  const a = (1 - x) / 2;
  const b = (1 + x) / 2;
  return [Math.sin((a * Math.PI) / 2), Math.sin((b * Math.PI) / 2)];
}

class Deck {
  constructor(ctx, master, label = "A") {
    this.ctx = ctx;
    this.master = master;
    this.label = label;
    this.listeners = new Map();

    // Underlying audio element — created on loadFile.
    this.audioEl = null;
    this.mediaSrc = null;

    // Channel strip: source → preGain → low → mid → high → filter → chanFader → xfadeGain → master
    this.preGain   = ctx.createGain();   this.preGain.gain.value = 1;
    this.low       = ctx.createBiquadFilter(); this.low.type = "lowshelf"; this.low.frequency.value = 200;  this.low.gain.value = 0;
    this.mid       = ctx.createBiquadFilter(); this.mid.type = "peaking";  this.mid.frequency.value = 1000; this.mid.Q.value = 0.9; this.mid.gain.value = 0;
    this.high      = ctx.createBiquadFilter(); this.high.type = "highshelf"; this.high.frequency.value = 4000; this.high.gain.value = 0;
    this.filter    = ctx.createBiquadFilter(); this.filter.type = "lowpass"; this.filter.frequency.value = 22050; this.filter.Q.value = 0.7;
    this.chanFader = ctx.createGain();    this.chanFader.gain.value = 1;
    this.xfadeGain = ctx.createGain();    this.xfadeGain.gain.value = 1;

    this.preGain.connect(this.low);
    this.low.connect(this.mid);
    this.mid.connect(this.high);
    this.high.connect(this.filter);
    this.filter.connect(this.chanFader);
    this.chanFader.connect(this.xfadeGain);
    this.xfadeGain.connect(this.master);

    // Phase A R11 A — per-deck echo send. Taps post-EQ/pre-xfader so that
    // during an "Echo out + cut in" transition we can route this deck's
    // audio into the master echo FX wet bus while the crossfader silences
    // the dry signal. Default gain 0 (no send); `engine.transition()`
    // ramps it 0→1 during the first half of the transition and back to 0
    // over the 2-second tail. The destination is wired in DjEngine after
    // FX rack creation.
    this.echoSend = ctx.createGain();
    this.echoSend.gain.value = 0;
    this.chanFader.connect(this.echoSend);

    this.analyser = ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.xfadeGain.connect(this.analyser);

    // State
    this.duration = 0;
    this.bpm = 0;
    this.title = "";
    this.hotCues = [null, null, null, null, null, null, null, null]; // 8 cues
    this.loop = { start: null, end: null, enabled: false };
    this._loopRaf = null;
    this.loopMomentary = false;
    this.pitch = 0;
    // Phantom playhead — where the deck would be if no temp action had occurred.
    this.virtualStartPerf = null; // performance.now() at virtual reference
    this.virtualStartPos  = null; // currentTime at virtual reference
    // Slip / scratch state
    this.slipActive = false;
    this.scratchActive = false;
    // EQ kill saved values (so we can restore on toggle)
    this.killSaved = { low: null, mid: null, high: null };
    // Crossfader assign — A | B | thru (default A for deckA/C, B for deckB/D)
    this.xfaderAssign = (label === "A" || label === "C") ? "A" : "B";
    // Tap-tempo state
    this._tapTimes = [];
    // First-beat phase estimate (used by downbeat indicator)
    this.firstBeatTime = 0;
  }

  on(event, cb) {
    const arr = this.listeners.get(event) || [];
    arr.push(cb);
    this.listeners.set(event, arr);
  }
  _emit(event, ...args) {
    (this.listeners.get(event) || []).forEach((cb) => { try { cb(...args); } catch {/**/} });
  }

  // ---------- transport ----------
  get currentTime()  { return this.audioEl?.currentTime || 0; }
  get playing()      { return !!this.audioEl && !this.audioEl.paused; }
  get playbackRate() { return this.audioEl?.playbackRate || 1; }
  get effectiveBpm() { return this.bpm ? this.bpm * (1 + (this.pitch || 0) / 100) : 0; }

  getLevel() {
    const buf = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(buf);
    let s = 0, p = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      s += v * v; const a = Math.abs(v); if (a > p) p = a;
    }
    return { rms: Math.sqrt(s / buf.length), peak: p };
  }

  // Reset virtual playhead reference to "now"
  _resetVirtual() {
    this.virtualStartPerf = performance.now();
    this.virtualStartPos  = this.currentTime;
  }
  // Compute current virtual playhead (real time if no slip event in progress)
  getVirtualPosition() {
    if (this.virtualStartPerf == null) return this.currentTime;
    const elapsed = (performance.now() - this.virtualStartPerf) / 1000;
    return this.virtualStartPos + elapsed * this.playbackRate;
  }

  async play() {
    if (!this.audioEl) {
      // eslint-disable-next-line no-console
      console.warn(`[deck ${this.label}] play() called but no audioEl yet (loadFile may still be pending)`);
      return;
    }
    // Resume AudioContext if suspended (autoplay policy)
    if (this.ctx && this.ctx.state === "suspended") {
      try { await this.ctx.resume(); } catch { /* */ }
    }
    // eslint-disable-next-line no-console
    console.log(`[deck ${this.label}] play() called, ctx.state=${this.ctx?.state}, audioEl.paused=${this.audioEl.paused}, audioEl.readyState=${this.audioEl.readyState}`);
    try {
      const p = this.audioEl.play();
      if (p && typeof p.then === "function") {
        await p;
      }
      // eslint-disable-next-line no-console
      console.log(`[deck ${this.label}] play() resolved, deck.playing=${this.playing}`);
      this._emit("playchange", true);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[deck ${this.label}] play() REJECTED:`, err?.name, err?.message);
      try { window.dispatchEvent(new CustomEvent("sr-toast", { detail: { type: "error", msg: `DJ deck ${this.label} play blocked: ${err?.message || err?.name || "autoplay"}` } })); } catch { /* */ }
      throw err; // Propagate so Auto-DJ tick can handle it
    }
  }
  pause() {
    if (!this.audioEl) return;
    try { this.audioEl.pause(); this._emit("playchange", false); } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`[deck ${this.label}] pause() threw:`, err);
    }
  }
  cue() {
    if (!this.audioEl) return;
    this.audioEl.currentTime = 0;
    this.audioEl.pause();
    this._emit("playchange", false);
    this._emit("positionchange", 0);
  }
  seekTo(t) {
    if (!this.audioEl) return;
    this.audioEl.currentTime = clamp(t, 0, this.duration || 0);
    this._emit("positionchange", this.audioEl.currentTime);
  }
  setGain(v)     { this.preGain.gain.setTargetAtTime(clamp(v, 0, 2),    this.ctx.currentTime, 0.02); }
  setChannel(v)  { this.chanFader.gain.setTargetAtTime(clamp(v, 0, 1.2),this.ctx.currentTime, 0.02); }
  setHigh(dB)    { this.high.gain.setTargetAtTime(clamp(dB * 24, -24, 12), this.ctx.currentTime, 0.02); }
  setMid(dB)     { this.mid.gain.setTargetAtTime(clamp(dB * 24, -24, 12),  this.ctx.currentTime, 0.02); }
  setLow(dB)     { this.low.gain.setTargetAtTime(clamp(dB * 24, -24, 12),  this.ctx.currentTime, 0.02); }
  setFilter(v) {
    // v in [-1, 1]. Negative = LPF down, positive = HPF up.
    if (v < 0) {
      this.filter.type = "lowpass";
      const f = 22050 * Math.pow(10, v * 1.5);
      this.filter.frequency.setTargetAtTime(clamp(f, 80, 22050), this.ctx.currentTime, 0.03);
    } else if (v > 0) {
      this.filter.type = "highpass";
      const f = 40 + (22050 - 40) * v;
      this.filter.frequency.setTargetAtTime(clamp(f, 40, 22050), this.ctx.currentTime, 0.03);
    } else {
      this.filter.type = "lowpass";
      this.filter.frequency.setTargetAtTime(22050, this.ctx.currentTime, 0.05);
    }
  }
  // EQ kill helpers
  killEq(band) {
    const node = band === "low" ? this.low : band === "mid" ? this.mid : this.high;
    if (this.killSaved[band] != null) {
      node.gain.setTargetAtTime(this.killSaved[band], this.ctx.currentTime, 0.01);
      this.killSaved[band] = null;
    } else {
      this.killSaved[band] = node.gain.value;
      node.gain.setTargetAtTime(-24, this.ctx.currentTime, 0.01);
    }
    return this.killSaved[band] == null; // returns isKilled state? actually it's now NOT killed if we just restored. Return active state:
  }
  isEqKilled(band) { return this.killSaved[band] != null; }

  // Pitch ±8% (changes both rate AND tempo — DJ pitch fader semantics)
  setPitch(percent) {
    const p = clamp(percent, -8, 8);
    this.pitch = p;
    if (!this.audioEl) return;
    this.audioEl.playbackRate = 1 + p / 100;
    this.audioEl.preservesPitch = false;
    this._emit("pitchchange", p);
  }
  matchBPM(target) {
    if (!this.bpm || !target) return false;
    const ratio = clamp(target / this.bpm, 0.92, 1.08);
    this.setPitch((ratio - 1) * 100);
    return true;
  }
  sync(otherBPM) {
    if (!this.bpm || !otherBPM) return;
    const ratio = clamp(otherBPM / this.bpm, 0.92, 1.08);
    this.setPitch((ratio - 1) * 100);
  }

  // ---------- hot cues (8 slots, slip-aware) ----------
  setHotCue(slot) {
    if (!this.audioEl) return;
    this.hotCues[slot] = this.audioEl.currentTime;
    this._emit("cueschange", this.hotCues);
  }
  jumpHotCue(slot) {
    const t = this.hotCues[slot];
    if (t == null || !this.audioEl) return;
    if (this.slipActive) {
      // Save virtual playhead reference BEFORE the jump so we can come back.
      this._resetVirtual();
    }
    this.audioEl.currentTime = t;
    this._emit("positionchange", t);
  }
  clearHotCue(slot) {
    this.hotCues[slot] = null;
    this._emit("cueschange", this.hotCues);
  }
  // When slip is on, "releasing" a hot cue (pointerup after hold) returns to phantom.
  releaseHotCue() {
    if (!this.slipActive || this.virtualStartPerf == null || !this.audioEl) return;
    const v = this.getVirtualPosition();
    this.audioEl.currentTime = clamp(v, 0, this.duration || 0);
    this.virtualStartPerf = null;
    this._emit("positionchange", this.audioEl.currentTime);
  }

  // ---------- loop rolls ----------
  setLoop(start, end) { this.loop.start = start; this.loop.end = end; }
  toggleLoop() {
    if (this.loop.enabled) this.releaseLoop();
    else if (this.loop.start != null && this.loop.end != null) this._engageLoop(false);
  }
  // beats: 1/32 → 4 (fractional values like 0.03125 supported).
  // momentary: true = press-and-hold, false = sustained.
  startLoop(beats, momentary = false) {
    if (!this.audioEl) return;
    const beatSec = this.bpm ? (60 / this.effectiveBpm) : 0.5;
    const sec = beatSec * beats;
    this.loop.start = this.audioEl.currentTime;
    this.loop.end   = this.loop.start + sec;
    this._engageLoop(momentary);
  }
  _engageLoop(momentary) {
    this.loop.enabled = true;
    this.loopMomentary = !!momentary;
    this._resetVirtual();
    const tick = () => {
      if (!this.loop.enabled || !this.audioEl) return;
      if (this.audioEl.currentTime >= this.loop.end) {
        this.audioEl.currentTime = this.loop.start;
      }
      this._loopRaf = requestAnimationFrame(tick);
    };
    this._loopRaf = requestAnimationFrame(tick);
    this._emit("loopchange", { ...this.loop });
  }
  releaseLoop() {
    if (!this.loop.enabled) return;
    const wasMomentary = this.loopMomentary;
    this.loop.enabled = false;
    this.loopMomentary = false;
    if (this._loopRaf) { cancelAnimationFrame(this._loopRaf); this._loopRaf = null; }
    if ((wasMomentary || this.slipActive) && this.virtualStartPerf != null && this.audioEl) {
      const v = this.getVirtualPosition();
      this.audioEl.currentTime = clamp(v, 0, this.duration || 0);
      this._emit("positionchange", this.audioEl.currentTime);
    }
    this.virtualStartPerf = null;
    this._emit("loopchange", { ...this.loop });
  }

  // ---------- scratch (jog) ----------
  enterScratch() {
    if (!this.audioEl) return;
    this.scratchActive = true;
    this._resetVirtual();
  }
  exitScratch() {
    if (!this.audioEl) return;
    this.scratchActive = false;
    if (this.slipActive && this.virtualStartPerf != null) {
      const v = this.getVirtualPosition();
      this.audioEl.currentTime = clamp(v, 0, this.duration || 0);
      this._emit("positionchange", this.audioEl.currentTime);
    }
    this.virtualStartPerf = null;
    // Restore normal playback rate (clear any scratch-induced rate change)
    this.audioEl.playbackRate = 1 + (this.pitch || 0) / 100;
  }
  scratchNudge(delta) {
    if (!this.audioEl) return;
    const newRate = clamp(1 + delta, -3, 3);
    if (Math.abs(newRate) < 0.05) this.audioEl.playbackRate = 0.05;
    else this.audioEl.playbackRate = newRate;
  }

  // ---------- slip toggle ----------
  setSlip(active) { this.slipActive = !!active; this._emit("slipchange", this.slipActive); }

  // ---------- tap-tempo (fallback for unknown BPM) ----------
  tapTempo() {
    const now = performance.now();
    this._tapTimes.push(now);
    // Keep only last 8 taps within 4 seconds
    this._tapTimes = this._tapTimes.filter((t) => now - t < 4000).slice(-8);
    if (this._tapTimes.length >= 2) {
      const intervals = [];
      for (let i = 1; i < this._tapTimes.length; i++) {
        intervals.push(this._tapTimes[i] - this._tapTimes[i - 1]);
      }
      const avgMs = intervals.reduce((a, b) => a + b, 0) / intervals.length;
      const bpm = 60000 / avgMs;
      if (bpm > 40 && bpm < 250) {
        this.bpm = Math.round(bpm * 10) / 10;
        this.firstBeatTime = this.currentTime; // anchor downbeat to last tap
        this._emit("bpm", this.bpm);
      }
    }
    return this.bpm;
  }
  // Manually set the downbeat phase anchor (for "Tap downbeat" UI).
  setDownbeatAnchor() {
    this.firstBeatTime = this.currentTime;
    this._emit("downbeat", this.firstBeatTime);
  }
  // Compute current beat within measure (0..3) for the downbeat indicator.
  beatPhase() {
    if (!this.bpm) return -1;
    const beatSec = 60 / this.effectiveBpm;
    const elapsed = this.currentTime - (this.firstBeatTime || 0);
    if (elapsed < 0) return 0;
    const beatIdx = Math.floor(elapsed / beatSec);
    return ((beatIdx % 4) + 4) % 4;
  }

  // ---------- load ----------
  async loadFile(file) {
    // eslint-disable-next-line no-console
    console.log(`[deck ${this.label}] loadFile entry, file=${file?.name} size=${file?.size} type=${file?.type} ctx.state=${this.ctx?.state}`);
    if (this.audioEl) {
      try { this.audioEl.pause(); } catch {/**/}
      try { this.mediaSrc?.disconnect(); } catch {/**/}
    }
    const url = URL.createObjectURL(file);
    const el = new Audio();
    el.src = url;
    el.crossOrigin = "anonymous";
    el.preservesPitch = false;
    this.audioEl = el;
    this.mediaSrc = this.ctx.createMediaElementSource(el);
    this.mediaSrc.connect(this.preGain);

    const duration = await new Promise((res) => {
      el.onloadedmetadata = () => res(el.duration || 0);
      el.onerror = () => res(0);
      setTimeout(() => res(el.duration || 0), 5000);
    });
    // eslint-disable-next-line no-console
    console.log(`[deck ${this.label}] loadFile metadata ready, duration=${duration} audioEl.readyState=${el.readyState}`);
    this.duration = duration;
    this.title = file.name.replace(/\.[^/.]+$/, "");
    this.bpm = 0;
    this.detectStatus = "pending";  // 'pending' | 'detecting' | 'ok' | 'failed'
    this.detectedKey = null;        // Camelot key, e.g. "8A"
    this.pitch = 0;
    this.hotCues = [null, null, null, null, null, null, null, null];
    this.cueNames = ["Cue 1","Cue 2","Cue 3","Cue 4","Cue 5","Cue 6","Cue 7","Cue 8"];
    this.loop = { start: null, end: null, enabled: false };
    this.firstBeatTime = 0;
    this._emit("loaded", { duration, title: this.title, url });

    el.addEventListener("timeupdate", () => this._emit("positionchange", el.currentTime));

    // Phase A R3 — B1 fix: kick off BPM detection automatically so the
    // deck UI never gets stuck on `--`. Run async, surface status events
    // so the UI can render "detecting…" / "failed" feedback.
    this.detectStatus = "detecting";
    this._emit("bpm-status", this.detectStatus);
    this.detectBPM().then((bpm) => {
      this.detectStatus = bpm > 0 ? "ok" : "failed";
      this._emit("bpm-status", this.detectStatus);
      // Phase A R6 B2 — once PCM is cached (set inside detectBPM at line ~416),
      // run the waveform-band analysis worker and cache results on the deck.
      // MirroredWaveform reads `deck._bands` / `deck._bandsColumns` directly;
      // the pop-out's RemoteDeck receives the same payload via BC.
      this._computeBands().catch((err) => {
        // eslint-disable-next-line no-console
        console.warn(`[DjEngine ${this.label}] computeBands failed:`, err);
      });
    }).catch((err) => {
      this.detectStatus = "failed";
      this._emit("bpm-status", this.detectStatus);
      // eslint-disable-next-line no-console
      console.warn(`[DjEngine] BPM detect failed for ${this.label}:`, err);
    });
    // Also kick off Camelot key detection.
    this.detectKey().then((k) => {
      if (k) { this.detectedKey = k; this._emit("key", k); }
    }).catch(() => { /* silent */ });

    return { duration, title: this.title, url };
  }
  async detectBPM() {
    if (!this.audioEl?.src) return 0;
    try {
      // PHASE A R5 — decode on the MAIN thread (workers don't have
      // OfflineAudioContext / decodeAudioData), then pass the decoded
      // PCM samples to the worker for the cheap math.
      const resp = await fetch(this.audioEl.src);
      const buf = await resp.arrayBuffer();
      // Use a one-shot OfflineAudioContext for decode so we don't tie up the live ctx.
      const tmp = new OfflineAudioContext(1, 44100 * 60, 44100);
      const decoded = await tmp.decodeAudioData(buf);
      const sr = decoded.sampleRate;
      // Take up to 30 s of mono audio for analysis.
      const limit = Math.min(decoded.length, sr * 30);
      const mono = new Float32Array(limit);
      const ch0 = decoded.getChannelData(0);
      const ch1 = decoded.numberOfChannels > 1 ? decoded.getChannelData(1) : null;
      for (let i = 0; i < limit; i++) {
        mono[i] = ch1 ? (ch0[i] + ch1[i]) * 0.5 : ch0[i];
      }
      // Cache the PCM samples for the key worker / waveform worker so
      // they don't have to re-decode.
      this._pcm = mono;
      this._pcmSampleRate = sr;
      // eslint-disable-next-line no-console
      console.log(`[DjEngine ${this.label}] decoded ${limit} samples @ ${sr}Hz for BPM detect`);

      if (!this._bpmWorker) {
        this._bpmWorker = new Worker(new URL("./bpmWorker.js", import.meta.url));
      }
      const id = Math.random().toString(36).slice(2);
      const result = await new Promise((resolve) => {
        const onMsg = (e) => {
          if (e.data?.id !== id) return;
          this._bpmWorker.removeEventListener("message", onMsg);
          resolve(e.data);
        };
        this._bpmWorker.addEventListener("message", onMsg);
        // Send a *copy* of the PCM (so we can keep `this._pcm` for the
        // key worker). Transferring would detach the buffer.
        const samplesCopy = new Float32Array(mono);
        this._bpmWorker.postMessage(
          { id, samples: samplesCopy, sampleRate: sr },
          [samplesCopy.buffer]
        );
      });
      // eslint-disable-next-line no-console
      console.log(`[DjEngine ${this.label}] bpmWorker result:`, result);
      if (result?.error) {
        // eslint-disable-next-line no-console
        console.warn(`[DjEngine ${this.label}] bpmWorker error:`, result.error);
      }
      // Phase A R7 B4 — half-BPM autocorrelation fix. Modern dance music
      // sits in the 90-180 range; tracks detected below 80 are almost
      // always the bar-level periodicity (half-BPM) and tracks above
      // 180 are almost always doubled. Normalize into the musical range.
      let rawBpm = result?.bpm || 0;
      if (rawBpm > 0) {
        if (rawBpm < 80) rawBpm *= 2;
        while (rawBpm > 180) rawBpm /= 2;
        rawBpm = Math.max(60, Math.min(200, rawBpm));
        // eslint-disable-next-line no-console
        console.log(`[DjEngine ${this.label}] BPM normalized: ${result.bpm} → ${rawBpm}`);
      }
      this.bpm = rawBpm;
      if (result?.peakTime != null) this.firstBeatTime = result.peakTime;
      this._emit("bpm", this.bpm);
      return this.bpm;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[DjEngine ${this.label}] detectBPM threw:`, e);
      return 0;
    }
  }
  async detectKey() {
    if (!this._pcm) {
      // Decode hasn't happened yet — kick BPM first to populate cache.
      await this.detectBPM();
    }
    if (!this._pcm) return null;
    try {
      if (!this._keyWorker) {
        this._keyWorker = new Worker(new URL("./keyWorker.js", import.meta.url));
      }
      const id = Math.random().toString(36).slice(2);
      const samplesCopy = new Float32Array(this._pcm);
      const result = await new Promise((resolve) => {
        const onMsg = (e) => {
          if (e.data?.id !== id) return;
          this._keyWorker.removeEventListener("message", onMsg);
          resolve(e.data);
        };
        this._keyWorker.addEventListener("message", onMsg);
        this._keyWorker.postMessage(
          { id, samples: samplesCopy, sampleRate: this._pcmSampleRate },
          [samplesCopy.buffer]
        );
      });
      return result?.camelot || null;
    } catch (e) {
      return null;
    }
  }
  // Phase A R6 B2 — compute & cache band-amplitude columns for the
  // mirrored-waveform UI. Runs once per loaded track AFTER detectBPM has
  // populated `_pcm`. Cached results live on the deck (`_bands`,
  // `_bandsColumns`) so the React component reads them directly and the
  // useDjEngine layer can broadcast them to the pop-out.
  async _computeBands() {
    if (!this._pcm || !this._pcmSampleRate) return;
    try {
      if (!this._bandsWorker) {
        this._bandsWorker = new Worker(new URL("./waveformWorker.js", import.meta.url));
      }
      const id = Math.random().toString(36).slice(2);
      const samples = new Float32Array(this._pcm);
      const out = await new Promise((resolve) => {
        const onMsg = (e) => {
          if (e.data?.id !== id) return;
          this._bandsWorker.removeEventListener("message", onMsg);
          resolve(e.data);
        };
        this._bandsWorker.addEventListener("message", onMsg);
        this._bandsWorker.postMessage(
          { id, samples, sampleRate: this._pcmSampleRate, columns: 768 },
          [samples.buffer]
        );
      });
      if (out?.bands) {
        this._bands        = out.bands;
        this._bandsColumns = out.columns || 0;
        this._emit("bands-ready", { columns: this._bandsColumns });
        // eslint-disable-next-line no-console
        console.log(`[DjEngine ${this.label}] bands ready (${this._bandsColumns} cols)`);
      } else if (out?.error) {
        // eslint-disable-next-line no-console
        console.warn(`[DjEngine ${this.label}] waveform worker error:`, out.error);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(`[DjEngine ${this.label}] _computeBands threw:`, e);
    }
  }
}

/* ===================== FX RACK ===================== */
/**
 * Five-effect shared rack: Echo, Reverb, Delay, LPF, HPF.
 * Each FX taps from a chosen "send source" (A|B|C|D|Master) and returns
 * its wet signal to the engine master gain. Disabled FX have their wet
 * gain ramped to 0 (audio graph stays connected — no clicks).
 */
class FxRack {
  constructor(ctx, master, output, decks, engineFilters = {}) {
    this.ctx = ctx;
    this.master = master;
    // Phase A R7 B5 — wet path now feeds a downstream summer, NOT master.
    // Routing wet back into master created a feedback loop through the
    // master→tap→filter→wet chain (audible as a "screech" on filter Q).
    this.output = output || master;
    this.decks = decks;
    // Phase A R7 B5 — master LPF/HPF filters live on the engine in series
    // between master and outputSummer. FxRack drives their frequency value
    // when the UI toggles LPF/HPF on/off (INSERT semantics instead of
    // parallel send-return which is sonically wrong for filters).
    this.masterLpf = engineFilters.masterLpf || null;
    this.masterHpf = engineFilters.masterHpf || null;
    this.fx = {};

    // Master tap — must come BEFORE the connection to broadcastDest /
    // destination so the FX wet doesn't feed back into itself. We add a
    // parallel gain that reads master's output for echo/reverb/delay
    // sends. Filter sends now bypass this entirely (INSERT mode on the
    // engine-level master filters).
    this.masterTap = ctx.createGain();
    this.masterTap.gain.value = 1;
    this.master.connect(this.masterTap);

    this._makeFx("echo",   { time: 0.3,   feedback: 0.4, wet: 0.5 });
    this._makeFx("reverb", { size: 1.0,                 wet: 0.4 });
    this._makeFx("delay",  { time: 0.5,   feedback: 0.3, wet: 0.4 });
    this._makeFx("lpf",    { cutoff: 5000,              wet: 1.0 });
    this._makeFx("hpf",    { cutoff: 200,               wet: 1.0 });
  }

  _synthIR(durationSec = 2.0) {
    const sampleRate = this.ctx.sampleRate;
    const length = Math.floor(durationSec * sampleRate);
    const buf = this.ctx.createBuffer(2, length, sampleRate);
    for (let ch = 0; ch < 2; ch++) {
      const data = buf.getChannelData(ch);
      for (let i = 0; i < length; i++) {
        const t = i / length;
        const decay = Math.exp(-t * 4);
        data[i] = (Math.random() * 2 - 1) * decay;
      }
    }
    return buf;
  }

  _makeFx(name, opts) {
    const ctx = this.ctx;
    const input = ctx.createGain(); input.gain.value = 1;
    const wet   = ctx.createGain(); wet.gain.value = 0; // off by default
    const fx = { input, wet, enabled: false, sendFrom: "master", wetLevel: opts.wet, _connectedSource: null };

    if (name === "echo" || name === "delay") {
      const delay = ctx.createDelay(5.0); delay.delayTime.value = opts.time;
      const fb    = ctx.createGain();     fb.gain.value = opts.feedback;
      input.connect(delay);
      delay.connect(fb); fb.connect(delay);
      delay.connect(wet);
      Object.assign(fx, { delay, fb, time: opts.time, feedback: opts.feedback });
    } else if (name === "reverb") {
      const conv = ctx.createConvolver(); conv.buffer = this._synthIR(2.0);
      input.connect(conv); conv.connect(wet);
      Object.assign(fx, { conv, size: opts.size });
    } else if (name === "lpf" || name === "hpf") {
      // Phase A R7 B5 — INSERT-mode filters live on the engine master bus
      // (engine.masterLpf / engine.masterHpf). FxRack holds the cutoff
      // state + drives those filters' frequency on setEnabled / setParam.
      // We do NOT create a parallel biquad here. Keep `wet` as a dummy
      // (gain stays 0) so getState() still returns a consistent shape.
      Object.assign(fx, { cutoff: opts.cutoff });
    }

    wet.connect(this.output);    // R7 B5: was this.master — caused feedback loop
    this.fx[name] = fx;
    if (name !== "lpf" && name !== "hpf") {
      this._connectSource(name, "master");
    }
  }

  _connectSource(name, source) {
    const fx = this.fx[name]; if (!fx) return;
    if (fx._connectedSource) { try { fx._connectedSource.disconnect(fx.input); } catch {/**/} }
    let src = null;
    if (source === "master") src = this.masterTap;
    else src = this.decks[source]?.xfadeGain || null;
    if (src) { try { src.connect(fx.input); } catch {/**/} fx._connectedSource = src; }
    fx.sendFrom = source;
  }

  setEnabled(name, enabled) {
    const fx = this.fx[name]; if (!fx) return;
    fx.enabled = !!enabled;
    // Phase A R7 B5 — LPF/HPF are INSERT effects on the engine master bus.
    // When DISABLED, we set the master filter to passthrough frequency.
    // When ENABLED, we apply the user's cutoff value with a smooth ramp.
    if (name === "lpf" && this.masterLpf) {
      const target = enabled ? clamp(fx.cutoff, 200, 20000) : 20000;
      this.masterLpf.frequency.setTargetAtTime(target, this.ctx.currentTime, 0.02);
      return;
    }
    if (name === "hpf" && this.masterHpf) {
      const target = enabled ? clamp(fx.cutoff, 20, 18000) : 20;
      this.masterHpf.frequency.setTargetAtTime(target, this.ctx.currentTime, 0.02);
      return;
    }
    // Other effects keep the parallel send-return model.
    fx.wet.gain.setTargetAtTime(fx.enabled ? fx.wetLevel : 0, this.ctx.currentTime, 0.02);
  }
  setParam(name, param, value) {
    const fx = this.fx[name]; if (!fx) return;
    const t = this.ctx.currentTime;
    if (param === "wet") {
      fx.wetLevel = value;
      if (fx.enabled && name !== "lpf" && name !== "hpf") {
        fx.wet.gain.setTargetAtTime(value, t, 0.02);
      }
    } else if ((name === "echo" || name === "delay") && param === "time") {
      fx.delay.delayTime.setTargetAtTime(value, t, 0.02); fx.time = value;
    } else if ((name === "echo" || name === "delay") && param === "feedback") {
      fx.fb.gain.setTargetAtTime(clamp(value, 0, 0.9), t, 0.02); fx.feedback = value;
    } else if (name === "lpf" && param === "cutoff") {
      // Phase A R7 B5 — clamp to musical range, drive engine master LPF.
      const cf = clamp(value, 200, 20000);
      fx.cutoff = cf;
      if (fx.enabled && this.masterLpf) {
        this.masterLpf.frequency.setTargetAtTime(cf, t, 0.02);
      }
    } else if (name === "hpf" && param === "cutoff") {
      const cf = clamp(value, 20, 18000);
      fx.cutoff = cf;
      if (fx.enabled && this.masterHpf) {
        this.masterHpf.frequency.setTargetAtTime(cf, t, 0.02);
      }
    } else if (name === "reverb" && param === "size") {
      fx.size = value;
      fx.conv.buffer = this._synthIR(0.5 + 3.5 * clamp(value, 0, 1));
    }
  }
  setSendFrom(name, source) { this._connectSource(name, source); }
  getState() {
    const out = {};
    for (const [k, v] of Object.entries(this.fx)) {
      out[k] = {
        enabled: v.enabled, sendFrom: v.sendFrom, wetLevel: v.wetLevel,
        time: v.time, feedback: v.feedback, cutoff: v.cutoff, size: v.size,
      };
    }
    return out;
  }
}

/* ===================== ENGINE ===================== */

export class DjEngine {
  constructor() {
    const AC = window.AudioContext || window.webkitAudioContext;
    this.ctx = new AC();
    this.master = this.ctx.createGain(); this.master.gain.value = 0.8;

    // Phase A R7 B5 — Master LPF/HPF live in series on the post-master bus.
    // The previous parallel send-return architecture in FxRack created a
    // feedback loop (master → masterTap → filter → wet → master) which
    // oscillated through a biquad's resonance and produced the user-
    // reported "screech" the moment LPF was toggled on. The fix is two
    // structural pieces:
    //   1. INSERT mode for filters: master → masterLpf → masterHpf → out.
    //      When disabled, frequencies are at passthrough (LPF=20kHz,
    //      HPF=20Hz) so the filter is sonically a unity gain.
    //   2. New outputSummer node downstream of master. The FxRack now
    //      routes echo/reverb/delay wet into outputSummer (NOT master),
    //      so even those parallel sends no longer feed back. Master is
    //      the FX rack's input-only "tap" source.
    this.masterLpf = this.ctx.createBiquadFilter();
    this.masterLpf.type = "lowpass";
    this.masterLpf.frequency.value = 20000; // passthrough when disabled
    this.masterLpf.Q.value = 1;             // tame; spec caps at 4
    this.masterHpf = this.ctx.createBiquadFilter();
    this.masterHpf.type = "highpass";
    this.masterHpf.frequency.value = 20;    // passthrough when disabled
    this.masterHpf.Q.value = 1;

    this.outputSummer = this.ctx.createGain();
    this.outputSummer.gain.value = 1.0;

    // Chain: master → masterLpf → masterHpf → outputSummer
    this.master.connect(this.masterLpf);
    this.masterLpf.connect(this.masterHpf);
    this.masterHpf.connect(this.outputSummer);

    // outputSummer feeds the final destinations.
    this.broadcastDest = this.ctx.createMediaStreamDestination();
    this.outputSummer.connect(this.broadcastDest);
    this.outputSummer.connect(this.ctx.destination);

    this.analyser = this.ctx.createAnalyser();
    this.analyser.fftSize = 256;
    this.outputSummer.connect(this.analyser);

    // Phase A R2 — 4 decks. A and C default to xfader assign A, B and D to B.
    this.deckA = new Deck(this.ctx, this.master, "A");
    this.deckB = new Deck(this.ctx, this.master, "B");
    this.deckC = new Deck(this.ctx, this.master, "C");
    this.deckD = new Deck(this.ctx, this.master, "D");

    this.crossfader = 0;
    this.crossCurve = "smooth";
    this.applyCrossfader();

    // FX rack — references all 4 decks for per-deck send-from selection.
    // Phase A R7 B5: now also receives `outputSummer` (where wet routes,
    // not back into master) and engine refs to drive master LPF/HPF in
    // INSERT mode.
    this.fxRack = new FxRack(this.ctx, this.master, this.outputSummer, {
      A: this.deckA, B: this.deckB, C: this.deckC, D: this.deckD,
    }, { masterLpf: this.masterLpf, masterHpf: this.masterHpf });

    // Phase A R11 A — wire each deck's `echoSend` (post-EQ/pre-xfader tap)
    // to the master echo FX input. Default send gain is 0, so this adds
    // no signal until `engine.transition(..., 'echo', ...)` ramps it up.
    // The masterTap → fx.echo.input connection already exists from FxRack
    // setup; per-deck sends are parallel inputs.
    try {
      [this.deckA, this.deckB, this.deckC, this.deckD].forEach((d) => {
        try { d.echoSend?.connect(this.fxRack.fx.echo.input); } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[engine] echoSend wire for deck ${d.label} failed:`, err);
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[engine] echoSend wiring failed:", err);
    }

    // Phase A R5 followup — Auto-DJ playlist lives on the engine (single
    // source of truth). Both the inline DjConsole2 and the pop-out
    // DjConsole2 read this state. Pop-out mutations are proxied via
    // BroadcastChannel; engine emits `autodj-change` on every mutation.
    this.autoDj = {
      playlist: [],          // [{trackId, name, durationSec, detectedBpm, detectedKey, file, sourceHandle?}]
      active: false,         // toggle ON/OFF
      currentIndex: 0,       // index of currently-playing playlist item
      crossfadeSec: 8,
      keyMatchEnabled: true,
      bpmMatchEnabled: true,
      loopPlaylist: false,
      maxLengthSec: 0,       // 0 → no cap
    };
    this._autoDjListeners = [];
  }
  onAutoDj(cb) { this._autoDjListeners.push(cb); return () => { this._autoDjListeners = this._autoDjListeners.filter(x => x !== cb); }; }
  _emitAutoDj() { this._autoDjListeners.forEach((cb) => { try { cb(this.autoDj); } catch {/**/} }); }
  addToPlaylist(track) {
    if (!track || !track.trackId) return "invalid";
    // De-dupe by trackId — the same Library row shouldn't appear twice.
    if (this.autoDj.playlist.some((t) => t.trackId === track.trackId)) return "duplicate";
    this.autoDj.playlist = [...this.autoDj.playlist, track];
    this._emitAutoDj();
    return "added";
  }
  removeFromPlaylist(index) {
    if (index < 0 || index >= this.autoDj.playlist.length) return;
    this.autoDj.playlist = this.autoDj.playlist.filter((_, i) => i !== index);
    if (this.autoDj.currentIndex >= this.autoDj.playlist.length) {
      this.autoDj.currentIndex = Math.max(0, this.autoDj.playlist.length - 1);
    }
    this._emitAutoDj();
  }
  reorderPlaylist(from, to) {
    if (from === to) return;
    const next = [...this.autoDj.playlist];
    const [it] = next.splice(from, 1);
    next.splice(to, 0, it);
    this.autoDj.playlist = next;
    this._emitAutoDj();
  }
  attachPlaylistFile(trackId, file) {
    const idx = this.autoDj.playlist.findIndex((t) => t.trackId === trackId);
    if (idx < 0) return;
    this.autoDj.playlist[idx] = { ...this.autoDj.playlist[idx], file, needsReattach: false };
    this._emitAutoDj();
  }
  setAutoDj(patch) {
    this.autoDj = { ...this.autoDj, ...patch };
    this._emitAutoDj();
  }
  async resume() { if (this.ctx.state === "suspended") await this.ctx.resume(); }

  setCrossfader(v) { this.crossfader = clamp(v, -1, 1); this.applyCrossfader(); }
  setCrossfaderCurve(mode) { this.crossCurve = mode; this.applyCrossfader(); }

  /**
   * Phase A R5 F1 — Manual crossfade trigger.
   * - If autoDj active: returns 'autodj' so the caller can ask AutoDjPanel
   *   to advance immediately. (We can't trigger Auto-DJ transitions from
   *   the engine alone — that lives in the panel state machine.)
   * - Else if both A and B are loaded: animate the crossfader from its
   *   current position to the opposite extreme over `durationSec`.
   * - Else: returns 'no-op'.
   */
  mixNow(durationSec = 8) {
    if (this._mixingNow) return "busy";
    const haveA = this.deckA?.audioEl && (this.deckA.duration || 0) > 0;
    const haveB = this.deckB?.audioEl && (this.deckB.duration || 0) > 0;
    if (!haveA || !haveB) return "no-op";
    // Direction: if A is louder right now (xfader <= 0) → fade to B (+1); else to A (-1).
    const target = this.crossfader <= 0 ? 1 : -1;
    const steps = 30;
    const stepMs = Math.max(20, (durationSec * 1000) / steps);
    this._mixingNow = true;
    try { this.deckB.play(); } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[mixNow] deckB.play threw:", err);
    }
    try { this.deckA.play(); } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[mixNow] deckA.play threw:", err);
    }
    const start = this.crossfader;
    let i = 0;
    const tick = () => {
      i++;
      const v = start + (target - start) * (i / steps);
      this.setCrossfader(v);
      if (i >= steps) {
        this._mixingNow = false;
        // Pause the outgoing deck so we don't keep two streams hot.
        try { (target > 0 ? this.deckA : this.deckB).pause(); } catch (err) {
          // eslint-disable-next-line no-console
          console.error("[mixNow] outgoing deck pause threw:", err);
        }
        return;
      }
      setTimeout(tick, stepMs);
    };
    setTimeout(tick, stepMs);
    return "started";
  }
  applyCrossfader() {
    const [gA, gB] = crossfaderGains(this.crossfader, this.crossCurve);
    const apply = (deck) => {
      const target = deck.xfaderAssign === "A" ? gA
                   : deck.xfaderAssign === "B" ? gB
                   : 1; // thru
      deck.xfadeGain.gain.setTargetAtTime(target, this.ctx.currentTime, 0.01);
    };
    apply(this.deckA); apply(this.deckB); apply(this.deckC); apply(this.deckD);
  }
  /**
   * Phase A R11 A + B — single entry point for non-linear transitions.
   * Linear / eqswap / filter styles continue to be orchestrated by
   * AutoDjPanel.runCrossfade per-step (they only need setCrossfader +
   * setLow + setFilter which already proxy fine through RemoteEngine).
   * Echo and Loop need engine-level FX wet-bus modulation + true loop
   * locking, so we run them here in the MAIN tab where the audio graph
   * lives. The popout's RemoteEngine.transition posts a BC command and
   * awaits this method's completion ack.
   *
   * `style`: 'echo' | 'loop'
   * Returns a Promise that resolves when the transition is complete.
   */
  async transition(fromLabel, toLabel, style, dur) {
    const fromDeck = this._deck(fromLabel);
    const toDeck   = this._deck(toLabel);
    if (!fromDeck || !toDeck) {
      // eslint-disable-next-line no-console
      console.warn(`[transition] missing decks from=${fromLabel} to=${toLabel}`);
      return;
    }
    const durSec = Math.max(2, dur || 8);
    // eslint-disable-next-line no-console
    console.log(`[transition] start style=${style} from=${fromLabel} to=${toLabel} dur=${durSec}s ctx=${this.ctx.state}`);

    if (style === "echo") {
      // ----- Echo Out + Cut In -----
      const echo = this.fxRack?.fx?.echo;
      if (!echo) {
        // eslint-disable-next-line no-console
        console.warn("[transition] echo FX node not found — falling back to linear");
        return this._linearCrossfade(fromLabel, toLabel, durSec);
      }
      const t0 = this.ctx.currentTime;
      const halfDur = durSec / 2;
      // Ramp ECHO WET 0→1 over halfDur (force enabled regardless of UI toggle).
      try {
        echo.enabled = true;
        echo.wet.gain.cancelScheduledValues(t0);
        echo.wet.gain.setValueAtTime(echo.wet.gain.value, t0);
        echo.wet.gain.linearRampToValueAtTime(0.7, t0 + halfDur);
      } catch (e) { /* eslint-disable-next-line no-console */ console.warn("[transition] echo wet ramp failed:", e); }
      // Ramp this DECK's send 0→1 over halfDur.
      try {
        fromDeck.echoSend.gain.cancelScheduledValues(t0);
        fromDeck.echoSend.gain.setValueAtTime(0, t0);
        fromDeck.echoSend.gain.linearRampToValueAtTime(1, t0 + halfDur);
      } catch (e) { /* eslint-disable-next-line no-console */ console.warn("[transition] echoSend ramp failed:", e); }

      // Move crossfader to silence outgoing's DRY over halfDur, in 15 steps.
      const xfDir = toLabel === "A" ? -1 : 1;
      const halfSteps = 15;
      for (let i = 1; i <= halfSteps; i++) {
        this.setCrossfader(this.crossfader + (xfDir - this.crossfader) * (i / halfSteps) * 0.5);
        await new Promise((r) => setTimeout(r, (halfDur * 1000) / halfSteps));
      }
      // t = dur/2 — start incoming at full gain (no fade).
      try {
        toDeck.play();
        // eslint-disable-next-line no-console
        console.log(`[transition] echo: incoming ${toLabel} play() at t=${halfDur}s`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[transition] echo: incoming ${toLabel} play failed:`, e);
      }
      // Move crossfader the rest of the way to incoming over the next halfDur.
      for (let i = 1; i <= halfSteps; i++) {
        this.setCrossfader(this.crossfader + (xfDir - this.crossfader) * (i / halfSteps));
        await new Promise((r) => setTimeout(r, (halfDur * 1000) / halfSteps));
      }
      this.setCrossfader(xfDir); // snap to extreme
      // Tail decay over 2s: ramp wet + send back to 0.
      const t1 = this.ctx.currentTime;
      try {
        echo.wet.gain.cancelScheduledValues(t1);
        echo.wet.gain.setValueAtTime(echo.wet.gain.value, t1);
        echo.wet.gain.linearRampToValueAtTime(0, t1 + 2);
        fromDeck.echoSend.gain.cancelScheduledValues(t1);
        fromDeck.echoSend.gain.setValueAtTime(fromDeck.echoSend.gain.value, t1);
        fromDeck.echoSend.gain.linearRampToValueAtTime(0, t1 + 2);
      } catch (e) { /* eslint-disable-next-line no-console */ console.warn("[transition] echo tail ramp failed:", e); }
      // Pause outgoing (audible audio is already gone from dry; only echo tail remains)
      try { fromDeck.pause(); } catch { /* */ }
      // After tail completes, disable echo (returning it to UI-toggle control).
      setTimeout(() => {
        try {
          echo.enabled = false;
          echo.wet.gain.setTargetAtTime(0, this.ctx.currentTime, 0.05);
        } catch { /* */ }
      }, 2100);
      // eslint-disable-next-line no-console
      console.log(`[transition] echo: complete (tail decaying)`);
      return;
    }

    if (style === "loop") {
      // ----- Loop + Swap -----
      // Outgoing locks into a 4-beat sustained loop at the current position.
      try {
        fromDeck.startLoop(4, { momentary: false });
        // eslint-disable-next-line no-console
        console.log(`[transition] loop: ${fromLabel} startLoop(4) at currentTime=${fromDeck.currentTime?.toFixed(2)}`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[transition] loop: startLoop failed:", e);
        return this._linearCrossfade(fromLabel, toLabel, durSec);
      }
      // Compute next downbeat of outgoing (measure boundary = 4 beats).
      const bpm = fromDeck.effectiveBpm || fromDeck.bpm || 120;
      const beatSec = 60 / bpm;
      const measureSec = beatSec * 4;
      const phase = ((fromDeck.currentTime || 0) - (fromDeck.firstBeatTime || 0)) % measureSec;
      const tToNextDownbeat = Math.max(0, measureSec - phase);
      // eslint-disable-next-line no-console
      console.log(`[transition] loop: waiting ${tToNextDownbeat.toFixed(3)}s to next downbeat (bpm=${bpm.toFixed(1)})`);
      await new Promise((r) => setTimeout(r, tToNextDownbeat * 1000));
      // Incoming starts at full gain on the downbeat.
      try {
        toDeck.play();
        // eslint-disable-next-line no-console
        console.log(`[transition] loop: incoming ${toLabel} play() on downbeat`);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(`[transition] loop: incoming ${toLabel} play failed:`, e);
      }
      // Crossfader sweeps outgoing 1→0 over `durSec` while loop holds.
      const xfDir = toLabel === "A" ? -1 : 1;
      const steps = 30;
      for (let i = 1; i <= steps; i++) {
        this.setCrossfader(this.crossfader + (xfDir - this.crossfader) * (i / steps));
        await new Promise((r) => setTimeout(r, (durSec * 1000) / steps));
      }
      this.setCrossfader(xfDir);
      // Release loop + pause outgoing.
      try { fromDeck.releaseLoop(); } catch { /* */ }
      try { fromDeck.pause(); } catch { /* */ }
      // eslint-disable-next-line no-console
      console.log(`[transition] loop: complete`);
      return;
    }

    // Unknown style — fall back to linear.
    return this._linearCrossfade(fromLabel, toLabel, durSec);
  }

  async _linearCrossfade(fromLabel, toLabel, durSec) {
    const xfDir = toLabel === "A" ? -1 : 1;
    const steps = 30;
    for (let i = 1; i <= steps; i++) {
      this.setCrossfader(this.crossfader + (xfDir - this.crossfader) * (i / steps));
      await new Promise((r) => setTimeout(r, (durSec * 1000) / steps));
    }
    try { this._deck(fromLabel)?.pause(); } catch { /* */ }
  }
  setDeckXfaderAssign(label, assign) {
    const deck = this._deck(label); if (!deck) return;
    deck.xfaderAssign = assign === "A" || assign === "B" || assign === "thru" ? assign : "thru";
    this.applyCrossfader();
  }
  _deck(label) {
    return label === "A" ? this.deckA : label === "B" ? this.deckB : label === "C" ? this.deckC : label === "D" ? this.deckD : null;
  }
  setMasterGain(v) { this.master.gain.setTargetAtTime(clamp(v, 0, 1.2), this.ctx.currentTime, 0.02); }
  readVU() {
    const buf = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteTimeDomainData(buf);
    let s = 0, p = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = (buf[i] - 128) / 128;
      s += v * v; const a = Math.abs(v); if (a > p) p = a;
    }
    return { rms: Math.sqrt(s / buf.length), peak: p };
  }
  getBroadcastTrack() { return this.broadcastDest?.stream?.getAudioTracks?.()[0] || null; }

  dispose() {
    try { this.fxRack = null; } catch {/**/}
    try { this.deckA.audioEl?.pause(); } catch {/**/}
    try { this.deckB.audioEl?.pause(); } catch {/**/}
    try { this.deckC.audioEl?.pause(); } catch {/**/}
    try { this.deckD.audioEl?.pause(); } catch {/**/}
    // R14.2-A — guard against double-close (Cannot close a closed AudioContext).
    if (this.ctx && this.ctx.state !== "closed") {
      try { this.ctx.close(); } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[DjEngine] ctx.close threw:", e?.message || e);
      }
    }
  }
}
